import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  pflegegrad: "",
  message: "",
};

export const Footer = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.pflegegrad) {
      toast.error("Bitte füllen Sie Name, E-Mail und Pflegegrad aus.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Wir melden uns in Kürze bei Ihnen.");
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Übermittlung fehlgeschlagen. Bitte versuchen Sie es erneut.";
      toast.error(typeof msg === "string" ? msg : "Übermittlung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full bg-white border border-[#1A1A1F]/[0.12] rounded-xl px-4 py-3 text-[#1A1A1F] placeholder-[#5A5F66]/50 focus:outline-none focus:border-[#3FA66D] focus:ring-1 focus:ring-[#3FA66D] transition";

  return (
    <footer
      id="kontakt"
      data-testid="footer-section"
      className="relative pt-24 pb-12 border-t border-[#1A1A1F]/[0.08] bg-gradient-to-b from-[#FAFAF7] to-[#E8F5EC]"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#3FA66D]/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3FA66D] mb-5">
              Kontakt
            </div>
            <h2
              data-testid="contact-headline"
              className="text-4xl sm:text-5xl font-medium tracking-tight leading-tight text-[#1A1A1F]"
            >
              <span className="text-gradient-dark">Sprechen Sie</span>{" "}
              <span className="text-gradient-green">mit uns.</span>
            </h2>
            <p className="mt-5 text-[#5A5F66] max-w-xl">
              Hinterlassen Sie uns eine Nachricht – wir helfen Ihnen
              persönlich beim Antrag und beraten unverbindlich.
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5A5F66] mb-2 block">
                  Name
                </label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className={inputBase}
                  placeholder="Ihr vollständiger Name"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5A5F66] mb-2 block">
                  E-Mail
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className={inputBase}
                  placeholder="ihre@email.de"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5A5F66] mb-2 block">
                  Telefon (optional)
                </label>
                <input
                  data-testid="contact-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className={inputBase}
                  placeholder="+49 ..."
                />
              </div>
              <div className="sm:col-span-1">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5A5F66] mb-2 block">
                  Pflegegrad
                </label>
                <Select
                  value={form.pflegegrad}
                  onValueChange={(v) => update("pflegegrad", v)}
                >
                  <SelectTrigger
                    data-testid="contact-select-pflegegrad"
                    className="w-full bg-white border border-[#1A1A1F]/[0.12] rounded-xl px-4 py-6 text-[#1A1A1F] data-[placeholder]:text-[#5A5F66]/50 focus:border-[#3FA66D] focus:ring-1 focus:ring-[#3FA66D]"
                  >
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#1A1A1F]/10 text-[#1A1A1F]">
                    <SelectItem value="1">Pflegegrad 1</SelectItem>
                    <SelectItem value="2">Pflegegrad 2</SelectItem>
                    <SelectItem value="3">Pflegegrad 3</SelectItem>
                    <SelectItem value="4">Pflegegrad 4</SelectItem>
                    <SelectItem value="5">Pflegegrad 5</SelectItem>
                    <SelectItem value="beantragt">Antrag läuft</SelectItem>
                    <SelectItem value="unsicher">Unsicher</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-[#5A5F66] mb-2 block">
                  Nachricht (optional)
                </label>
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  className={`${inputBase} resize-none`}
                  placeholder="Wie können wir Ihnen helfen?"
                />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between gap-4 mt-2">
                <p className="text-xs text-[#5A5F66] max-w-md">
                  Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zur
                  Kontaktaufnahme zu.
                </p>
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#3FA66D] text-white font-semibold hover:bg-[#368F5E] disabled:opacity-60 disabled:cursor-not-allowed transition-transform hover:scale-[1.03] shadow-[0_10px_28px_rgba(63,166,109,0.3)]"
                >
                  {loading ? "Wird gesendet…" : "Anfrage senden"}
                  {!loading && <Send size={16} />}
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl glass-mint p-8 sm:p-10 shadow-[0_20px_50px_-20px_rgba(63,166,109,0.25)]">
              <Logo height={28} />
              <p className="mt-6 text-[#1A1A1F]/80 leading-relaxed">
                pflege-box-gratis.de – Ihre monatliche Pflegebox, vollständig
                finanziert von der Pflegekasse. Persönlich, diskret und
                zuverlässig.
              </p>
              <div className="mt-8 space-y-4 text-[#1A1A1F]">
                <a
                  href="mailto:info@pflege-box-gratis.de"
                  className="flex items-center gap-3 hover:text-[#3FA66D] transition-colors break-all"
                  data-testid="footer-email"
                >
                  <Mail size={16} className="text-[#3FA66D] shrink-0" />
                  info@pflege-box-gratis.de
                </a>
                <a
                  href="tel:+4917670157687"
                  className="flex items-center gap-3 hover:text-[#3FA66D] transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone size={16} className="text-[#3FA66D]" />
                  0176 70157687
                </a>
                <div
                  className="flex items-center gap-3"
                  data-testid="footer-address"
                >
                  <MapPin size={16} className="text-[#3FA66D]" />
                  Mo – Fr · 08:00 – 18:00 Uhr
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#1A1A1F]/[0.08] flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#5A5F66]">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} pflege-box-gratis.de · Alle Rechte
            vorbehalten
          </div>
          <div className="flex items-center gap-6 flex-wrap justify-center">
            <Link
              to="/impressum"
              data-testid="footer-link-impressum"
              className="hover:text-[#1A1A1F] transition"
            >
              Impressum
            </Link>
            <Link
              to="/datenschutz"
              data-testid="footer-link-datenschutz"
              className="hover:text-[#1A1A1F] transition"
            >
              Datenschutz
            </Link>
            <Link
              to="/agb"
              data-testid="footer-link-agb"
              className="hover:text-[#1A1A1F] transition"
            >
              AGB
            </Link>
            <Link
              to="/widerruf"
              data-testid="footer-link-widerruf"
              className="hover:text-[#1A1A1F] transition"
            >
              Widerruf
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
